/**
 * Estimation Engine for RumahKira
 * Calculates preliminary construction budget based on house parameters
 */

import {
  COST_RATES,
  HOUSE_TYPES,
  FLOOR_MULTIPLIERS,
  BUDGET_ALLOCATION,
  MATERIAL_RATIOS,
  FINISHING_MATERIAL_MULTIPLIERS
} from './costRates';
import * as api from './api';

/**
 * Calculate the estimated construction budget
 * @param {Object} params - House parameters
 * @param {string} params.houseType - 'kampung', 'terrace', or 'bungalow'
 * @param {number} params.builtUpArea - Built-up area in square feet
 * @param {number} params.floors - Number of floors (1, 2, or 3)
 * @param {string} params.finishingLevel - 'basic', 'standard', or 'premium'
 * @returns {Object} - Estimation results
 */
export const calculateEstimation = (params) => {
  const { houseType, builtUpArea, floors, finishingLevel } = params;

  // Validate inputs
  if (!houseType || !builtUpArea || !floors || !finishingLevel) {
    throw new Error('Missing required parameters');
  }

  const houseTypeConfig = HOUSE_TYPES[houseType];
  const finishingConfig = COST_RATES[finishingLevel];
  const floorMultiplier = FLOOR_MULTIPLIERS[floors] || 1;

  if (!houseTypeConfig || !finishingConfig) {
    throw new Error('Invalid house type or finishing level');
  }

  // Calculate effective area (considering floors)
  const effectiveArea = builtUpArea * floorMultiplier;

  // Calculate cost per sqft with house type multiplier
  const minCostPerSqft = finishingConfig.min * houseTypeConfig.multiplier;
  const maxCostPerSqft = finishingConfig.max * houseTypeConfig.multiplier;
  const avgCostPerSqft = (minCostPerSqft + maxCostPerSqft) / 2;

  // Calculate total costs
  const minCost = Math.round(effectiveArea * minCostPerSqft);
  const maxCost = Math.round(effectiveArea * maxCostPerSqft);
  const avgCost = Math.round(effectiveArea * avgCostPerSqft);

  // Calculate budget breakdown
  const breakdown = calculateBreakdown(avgCost);

  // Calculate material estimates
  const materials = calculateMaterials(effectiveArea, finishingLevel, houseTypeConfig);

  return {
    costs: {
      minimum: minCost,
      maximum: maxCost,
      average: avgCost,
      perSqftMin: Math.round(minCostPerSqft),
      perSqftMax: Math.round(maxCostPerSqft)
    },
    breakdown,
    materials,
    inputs: {
      houseType,
      houseTypeLabel: houseTypeConfig.label,
      houseTypeLabelMy: houseTypeConfig.labelMy,
      builtUpArea,
      floors,
      finishingLevel,
      finishingLabel: finishingConfig.label,
      finishingLabelMy: finishingConfig.labelMy,
      effectiveArea
    },
    generatedAt: new Date().toISOString()
  };
};

/**
 * Calculate budget breakdown by category
 */
const calculateBreakdown = (totalCost) => {
  return {
    structure: {
      amount: Math.round(totalCost * (BUDGET_ALLOCATION.structure.percentage / 100)),
      percentage: BUDGET_ALLOCATION.structure.percentage,
      label: BUDGET_ALLOCATION.structure.label,
      labelMy: BUDGET_ALLOCATION.structure.labelMy,
      items: BUDGET_ALLOCATION.structure.items,
      itemsMy: BUDGET_ALLOCATION.structure.itemsMy
    },
    finishes: {
      amount: Math.round(totalCost * (BUDGET_ALLOCATION.finishes.percentage / 100)),
      percentage: BUDGET_ALLOCATION.finishes.percentage,
      label: BUDGET_ALLOCATION.finishes.label,
      labelMy: BUDGET_ALLOCATION.finishes.labelMy,
      items: BUDGET_ALLOCATION.finishes.items,
      itemsMy: BUDGET_ALLOCATION.finishes.itemsMy
    }
  };
};

/**
 * Calculate estimated material quantities
 */
const calculateMaterials = (effectiveArea, finishingLevel, houseTypeConfig) => {
  const finishingMultipliers = FINISHING_MATERIAL_MULTIPLIERS[finishingLevel] || FINISHING_MATERIAL_MULTIPLIERS.standard;
  const areaFactor = effectiveArea / 100; // Ratios are per 100 sqft
  const wallMultiplier = houseTypeConfig.wallRatio || 1;

  return {
    cement: {
      quantity: Math.round(MATERIAL_RATIOS.cement.bags * areaFactor),
      label: MATERIAL_RATIOS.cement.label,
      labelMy: MATERIAL_RATIOS.cement.labelMy,
      unit: MATERIAL_RATIOS.cement.unit,
      unitMy: MATERIAL_RATIOS.cement.unitMy
    },
    bricks: {
      quantity: Math.round(MATERIAL_RATIOS.bricks.units * areaFactor * wallMultiplier),
      label: MATERIAL_RATIOS.bricks.label,
      labelMy: MATERIAL_RATIOS.bricks.labelMy,
      unit: MATERIAL_RATIOS.bricks.unit,
      unitMy: MATERIAL_RATIOS.bricks.unitMy
    },
    steel: {
      quantity: parseFloat((MATERIAL_RATIOS.steel.tons * areaFactor).toFixed(1)),
      label: MATERIAL_RATIOS.steel.label,
      labelMy: MATERIAL_RATIOS.steel.labelMy,
      unit: MATERIAL_RATIOS.steel.unit,
      unitMy: MATERIAL_RATIOS.steel.unitMy
    },
    sand: {
      quantity: Math.round(MATERIAL_RATIOS.sand.cubicMeters * areaFactor),
      label: MATERIAL_RATIOS.sand.label,
      labelMy: MATERIAL_RATIOS.sand.labelMy,
      unit: MATERIAL_RATIOS.sand.unit,
      unitMy: MATERIAL_RATIOS.sand.unitMy
    },
    gravel: {
      quantity: Math.round(MATERIAL_RATIOS.gravel.cubicMeters * areaFactor),
      label: MATERIAL_RATIOS.gravel.label,
      labelMy: MATERIAL_RATIOS.gravel.labelMy,
      unit: MATERIAL_RATIOS.gravel.unit,
      unitMy: MATERIAL_RATIOS.gravel.unitMy
    },
    tiles: {
      quantity: Math.round(effectiveArea * MATERIAL_RATIOS.tiles.sqft * finishingMultipliers.tiles),
      label: MATERIAL_RATIOS.tiles.label,
      labelMy: MATERIAL_RATIOS.tiles.labelMy,
      unit: MATERIAL_RATIOS.tiles.unit,
      unitMy: MATERIAL_RATIOS.tiles.unitMy
    },
    paint: {
      quantity: Math.round(effectiveArea * MATERIAL_RATIOS.paint.liters * finishingMultipliers.paint * wallMultiplier),
      label: MATERIAL_RATIOS.paint.label,
      labelMy: MATERIAL_RATIOS.paint.labelMy,
      unit: MATERIAL_RATIOS.paint.unit,
      unitMy: MATERIAL_RATIOS.paint.unitMy
    }
  };
};

/**
 * Format currency in Malaysian Ringgit
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount).replace('MYR', 'RM');
};

/**
 * Format number with commas
 */
export const formatNumber = (num) => {
  return new Intl.NumberFormat('en-MY').format(num);
};

/**
 * Format date for display
 */
export const formatDate = (dateString, language = 'en') => {
  const date = new Date(dateString);
  const options = { day: 'numeric', month: 'long', year: 'numeric' };
  return date.toLocaleDateString(language === 'my' ? 'ms-MY' : 'en-MY', options);
};

/**
 * Save project - uses API if authenticated, otherwise localStorage
 */
export const saveProject = (projectData) => {
  // Save to localStorage (works for both authenticated and non-authenticated)
  const projects = getProjectsLocal();
  const projectId = projectData.id || `project_${Date.now()}`;

  const project = {
    ...projectData,
    id: projectId,
    savedAt: new Date().toISOString()
  };

  // Update or add project
  const existingIndex = projects.findIndex(p => p.id === projectId);
  if (existingIndex >= 0) {
    projects[existingIndex] = project;
  } else {
    projects.unshift(project);
  }

  // Keep only last 10 projects locally
  const trimmedProjects = projects.slice(0, 10);
  localStorage.setItem('rumahkira_projects', JSON.stringify(trimmedProjects));

  return project;
};

/**
 * Save project to API (async version)
 */
export const saveProjectToAPI = async (projectData) => {
  if (!api.isAuthenticated()) {
    return saveProject(projectData);
  }

  try {
    const apiData = {
      project_name: projectData.inputs?.projectName || 'My Project',
      project_type: projectData.type || 'quick',
      house_type: projectData.inputs?.houseType,
      built_up_area: projectData.inputs?.builtUpArea,
      floors: projectData.inputs?.floors,
      finishing_level: projectData.inputs?.finishingLevel,
      inputs: projectData.inputs || projectData,
      results: projectData
    };

    const response = await api.saveProject(apiData);

    if (response.success) {
      // Also save locally for offline access
      const localProject = {
        ...projectData,
        id: response.data.project_id,
        savedAt: new Date().toISOString()
      };
      saveProject(localProject);
      return { ...localProject, apiSaved: true, ...response.data };
    }
  } catch (error) {
    console.error('Failed to save to API:', error);
  }

  // Fallback to local save
  return saveProject(projectData);
};

/**
 * Get projects from localStorage
 */
export const getProjectsLocal = () => {
  try {
    const projects = localStorage.getItem('rumahkira_projects');
    return projects ? JSON.parse(projects) : [];
  } catch {
    return [];
  }
};

/**
 * Get all saved projects - combines API and local
 */
export const getProjects = () => {
  return getProjectsLocal();
};

/**
 * Get projects from API (async version)
 */
export const getProjectsFromAPI = async () => {
  if (!api.isAuthenticated()) {
    return getProjectsLocal();
  }

  try {
    const response = await api.getProjects();
    if (response.success && response.data.projects) {
      // Transform API projects to match local format
      return response.data.projects.map(p => ({
        id: p.id,
        inputs: typeof p.inputs === 'string' ? JSON.parse(p.inputs) : p.inputs,
        results: typeof p.results === 'string' ? JSON.parse(p.results) : p.results,
        type: p.project_type,
        savedAt: p.created_at,
        ...p
      }));
    }
  } catch (error) {
    console.error('Failed to fetch from API:', error);
  }

  // Fallback to local
  return getProjectsLocal();
};

/**
 * Delete a project
 */
export const deleteProject = (projectId) => {
  // Delete from local
  const projects = getProjectsLocal();
  const filtered = projects.filter(p => p.id !== projectId);
  localStorage.setItem('rumahkira_projects', JSON.stringify(filtered));
  return filtered;
};

/**
 * Delete project from API (async version)
 */
export const deleteProjectFromAPI = async (projectId) => {
  // Delete locally first
  deleteProject(projectId);

  if (!api.isAuthenticated()) {
    return true;
  }

  try {
    await api.deleteProject(projectId);
    return true;
  } catch (error) {
    console.error('Failed to delete from API:', error);
    return false;
  }
};

/**
 * Get a single project by ID
 */
export const getProject = (projectId) => {
  const projects = getProjectsLocal();
  return projects.find(p => p.id === projectId) || null;
};
